import { AddSquareIcon, TrashAltIcon } from "assets/icons";
import cn from "classnames";
import { DragDropList, Media } from "components";
import {
  IMAGE_FORMATS,
  VIDEO_FORMATS,
  MAX_IMAGE_FILE_SIZE,
  MAX_PRODUCT_IMAGES_LIMIT,
  MAX_VIDEO_FILE_SIZE,
  MAX_DOCUMENT_FILE_SIZE,
  DOCUMENT_FORMATS,
  BYTES_IN_MB
} from "constants/common";
import { MediaType } from "constants/enums";
import { getAcceptObjectForFilesInput, handleUploadFileError } from "helpers";
import { useImagesCompressor, useVideoCompressor } from "hooks";
import lodashFilter from "lodash/filter";
import lodashFindIndex from "lodash/findIndex";
import { useMemo } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { ClipLoader } from "react-spinners";

import { MultipleMediaFilesPickerProps } from "./MultipleMediaFilesPicker.types";

import styles from "./MultipleMediaFilesPicker.module.scss";

const MultipleMediaFilesPicker = ({
  label,
  allowedMediaTypes = [MediaType.IMAGE, MediaType.VIDEO],
  size = "md",
  value,
  acceptImageFormats = IMAGE_FORMATS,
  acceptVideoFormats = VIDEO_FORMATS,
  acceptDocumentFormats = DOCUMENT_FORMATS,
  filesLimit = MAX_PRODUCT_IMAGES_LIMIT,
  loadingFilesCounter,
  withCompression = true,
  customMaxFileSizeInMB,
  onDeleteMediaFile,
  onFilesDrop,
  onReorder,
  compressionOptions
}: MultipleMediaFilesPickerProps) => {
  const isImagesAllowed = allowedMediaTypes.includes(MediaType.IMAGE);
  const isVideosAllowed = allowedMediaTypes.includes(MediaType.VIDEO);
  const isDocumentsAllowed = allowedMediaTypes.includes(MediaType.DOCUMENT);

  const { compressImages, compressingImagesCount } =
    useImagesCompressor(compressionOptions);
  const {
    compressVideos,
    status: videoStatus,
    progress: videoProgress
  } = useVideoCompressor();
  const isProcessingVideo = videoStatus !== "idle";

  const filesLimitToUpload = filesLimit - value.length;

  const onDeleteMediaFileById = (mediaFileId: string) => {
    return () => {
      const mediaFileIndex = lodashFindIndex(value, { id: mediaFileId });
      onDeleteMediaFile(mediaFileIndex);
    };
  };

  const onDropImageRejected = (files: FileRejection[]) => {
    files.forEach((file) => {
      const message = file.errors[0].message;
      const code = file.errors[0].code;

      handleUploadFileError(
        message,
        code,
        allowedMediaTypes,
        customMaxFileSizeInMB
      );
    });
  };

  const handleDropAccepted = async (files: File[]) => {
    if (withCompression) {
      const compressed = await compressImages(files);
      const processed = await compressVideos(compressed);
      onFilesDrop(processed);
    } else {
      onFilesDrop(files);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: getAcceptObjectForFilesInput({
      acceptImageFormats,
      acceptVideoFormats,
      acceptDocumentFormats,
      isImagesAllowed,
      isVideosAllowed,
      isDocumentsAllowed
    }),
    maxSize: customMaxFileSizeInMB
      ? customMaxFileSizeInMB * BYTES_IN_MB
      : allowedMediaTypes.includes(MediaType.VIDEO)
        ? MAX_VIDEO_FILE_SIZE
        : allowedMediaTypes.includes(MediaType.IMAGE)
          ? MAX_IMAGE_FILE_SIZE
          : MAX_DOCUMENT_FILE_SIZE,
    maxFiles: filesLimitToUpload,
    noDrag: true,
    noKeyboard: true,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onDropAccepted: handleDropAccepted,
    onDropRejected: onDropImageRejected
  });

  const loadersList = Array.from({
    length:
      loadingFilesCounter ||
      compressingImagesCount ||
      (isProcessingVideo ? 1 : 0)
  });

  const filteredValuesByType = useMemo(() => {
    return lodashFilter(value, (mediaFile) => {
      return allowedMediaTypes.includes(mediaFile.mediaType);
    });
  }, [value, allowedMediaTypes]);

  return (
    <div className={styles.imagesPicker}>
      <DragDropList
        droppableId="media-files-picker"
        direction="horizontal"
        data={filteredValuesByType}
        isLoading={false}
        isDragDisabled={!onReorder}
        onChange={({ fromIndex, toIndex }) => onReorder?.(fromIndex, toIndex)}
        renderItem={(mediaFile) => (
          <div
            key={mediaFile.id}
            className={cn(
              styles.imagesPicker_imageThumb,
              styles[`imagesPicker_imageThumb-${size}`]
            )}
          >
            <Media
              className={styles.imagesPicker_image}
              withVideoLabel
              mediaFile={mediaFile}
            />

            <button
              className={styles.imagesPicker_deleteBtn}
              onClick={onDeleteMediaFileById(mediaFile.id)}
            >
              <TrashAltIcon width={16} heigh={16} fill={"#FFFFFF"} />
            </button>
          </div>
        )}
      />

      {isProcessingVideo && (
        <div
          className={cn(
            styles.imagesPicker_addBtn,
            styles[`imagesPicker_addBtn-${size}`],
            styles.imagesPicker_addBtn__loading
          )}
        >
          <div style={{ width: "80%", textAlign: "center" }}>
            <p
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#FB3973",
                marginBottom: 6
              }}
            >
              {videoStatus === "loading"
                ? "Preparing..."
                : `Optimizing video... ${videoProgress}%`}
            </p>
            <div
              style={{
                width: "100%",
                height: 6,
                borderRadius: 6,
                backgroundColor: "#F6F6FA"
              }}
            >
              <div
                style={{
                  width: `${videoProgress}%`,
                  height: "100%",
                  borderRadius: 6,
                  backgroundColor: "#FB3973",
                  transition: "width 0.3s"
                }}
              />
            </div>
          </div>
        </div>
      )}
      {loadersList.map((_, index) => (
        <div
          key={index}
          className={cn(
            styles.imagesPicker_addBtn,
            styles[`imagesPicker_addBtn-${size}`],
            styles.imagesPicker_addBtn__loading
          )}
        >
          <ClipLoader size={20} color="#FB3973" />
        </div>
      ))}
      {filesLimitToUpload > 0 ? (
        <div
          {...getRootProps()}
          className={cn(
            styles.imagesPicker_addBtn,
            styles[`imagesPicker_addBtn-${size}`]
          )}
        >
          <input {...getInputProps()} style={{ display: "none" }} />
          <AddSquareIcon color="#FB3973" width={20} height={20} />
          {label ?? null}
        </div>
      ) : null}
    </div>
  );
};

export default MultipleMediaFilesPicker;
